export const CHECK_SLUGS = ['time', 'money', 'talk'] as const;
export type CheckSlug = (typeof CHECK_SLUGS)[number];

export type CheckOption = { key: string; label: string };
export type CheckQuestion = {
  key: string;
  prompt: string;
  options: CheckOption[];
};

export type CheckQuiz = {
  slug: string;
  title: string;
  tagline: string;
  scaleLeft: string;
  scaleMid: string;
  scaleRight: string;
  questions: CheckQuestion[];
};

const ROLE_QUESTION: CheckQuestion = {
  key: 'role',
  prompt: 'You are the',
  options: [
    { key: 'mom', label: 'Mom' },
    { key: 'dad', label: 'Dad' },
    { key: 'other', label: 'Other' },
  ],
};

export const CHECK_QUIZZES: Record<CheckSlug, CheckQuiz> = {
  time: {
    slug: 'time',
    title: 'Parenting time',
    tagline: 'Where your nights sit next to other parents who checked.',
    scaleLeft: 'weekends',
    scaleMid: 'half',
    scaleRight: 'most nights',
    questions: [
      ROLE_QUESTION,
      {
        key: 'with_you',
        prompt: 'The kids are with you',
        options: [
          { key: 'most_nights', label: 'Most nights' },
          { key: 'about_half', label: 'About half' },
          { key: 'every_other_weekend', label: 'Every other weekend' },
          { key: 'holidays_summers', label: 'Holidays and summers' },
          { key: 'figuring_out', label: 'Still figuring it out' },
        ],
      },
      {
        key: 'schedule',
        prompt: 'Your schedule is',
        options: [
          { key: 'court_order', label: 'A court order' },
          { key: 'agreed', label: 'Something we agreed' },
          { key: 'informal', label: 'Informal' },
          { key: 'no_plan', label: "We don't really have one" },
        ],
      },
      {
        key: 'help',
        prompt: 'What would help most',
        options: [
          { key: 'shared_calendar', label: 'A calendar both can see' },
          { key: 'fewer_swaps', label: 'Fewer surprise swaps' },
          { key: 'whose_weekend', label: "Knowing whose weekend it is" },
        ],
      },
    ],
  },
  money: {
    slug: 'money',
    title: 'Expense sharing',
    tagline: 'Who covers extras, and how that compares.',
    scaleLeft: 'I cover it',
    scaleMid: 'we split',
    scaleRight: 'they cover it',
    questions: [
      ROLE_QUESTION,
      {
        key: 'who_pays',
        prompt: 'Who pays the extras (activities, medical, clothes)',
        options: [
          { key: 'mostly_me', label: 'Mostly me' },
          { key: 'we_split', label: 'We split' },
          { key: 'mostly_them', label: 'Mostly them' },
          { key: 'fight', label: "It's a fight every time" },
        ],
      },
      {
        key: 'track',
        prompt: 'How you track it',
        options: [
          { key: 'nothing', label: 'Nothing' },
          { key: 'texts', label: 'Texts and Venmo' },
          { key: 'spreadsheet', label: 'A spreadsheet' },
          { key: 'app', label: 'An app' },
        ],
      },
      {
        key: 'argument',
        prompt: 'The usual argument',
        options: [
          { key: 'who_pays', label: 'Who was supposed to pay' },
          { key: 'receipts', label: 'Missing receipts' },
          { key: 'support_vs_extras', label: 'Support vs extras' },
          { key: 'no_fight', label: "We don't argue about money" },
        ],
      },
    ],
  },
  talk: {
    slug: 'talk',
    title: 'Communication',
    tagline: 'How you show up in the hard texts.',
    scaleLeft: 'reactive',
    scaleMid: 'average',
    scaleRight: 'measured',
    questions: [
      ROLE_QUESTION,
      {
        key: 'when_need',
        prompt: 'When you need the other parent',
        options: [
          { key: 'short_written', label: 'Short and written' },
          { key: 'talk_everything', label: 'Still talk about everything' },
          { key: 'logistics_only', label: 'Logistics only' },
          { key: 'fight', label: 'It usually turns into a fight' },
        ],
      },
      {
        key: 'heated',
        prompt: 'A heated draft',
        options: [
          { key: 'wait_rewrite', label: 'I wait and rewrite' },
          { key: 'send', label: 'I send it' },
          { key: 'dont_answer', label: "I don't answer" },
          { key: 'show_friend', label: 'I show a friend first' },
        ],
      },
      {
        key: 'help',
        prompt: 'What would help most',
        options: [
          { key: 'no_regret', label: "Not sending the text you'll regret" },
          { key: 'a_record', label: 'A record of what was said' },
          { key: 'they_respond', label: 'Them actually responding' },
        ],
      },
    ],
  },
};

export function isCheckSlug(value: string): value is CheckSlug {
  return (CHECK_SLUGS as readonly string[]).includes(value);
}

export const RATE_SLUGS = ['rate_other', 'rate_self'] as const;
export type RateSlug = (typeof RATE_SLUGS)[number];

export const RATE_QUIZZES: Record<RateSlug, CheckQuiz> = {
  rate_other: {
    slug: 'rate_other',
    title: 'Rate my coparent',
    tagline: 'Four taps. A card you can share. We never ask for names.',
    scaleLeft: 'ghosted',
    scaleMid: 'mixed',
    scaleRight: 'shows up',
    questions: [
      ROLE_QUESTION,
      {
        key: 'when_text',
        prompt: 'When you text them',
        options: [
          { key: 'answer', label: 'They answer' },
          { key: 'hours', label: 'Hours later' },
          { key: 'dont', label: "They don't" },
          { key: 'fight', label: 'It becomes a fight' },
        ],
      },
      {
        key: 'pickup',
        prompt: 'Pickup and drop-off',
        options: [
          { key: 'on_time', label: 'On time' },
          { key: 'late', label: 'Usually late' },
          { key: 'flake', label: 'They flake' },
          { key: 'no_plan', label: "We don't have a plan" },
        ],
      },
      {
        key: 'usual',
        prompt: 'The usual issue',
        options: [
          { key: 'not_answering', label: 'Not answering' },
          { key: 'changing_plan', label: 'Changing the plan' },
          { key: 'money', label: 'Money' },
          { key: 'doing_okay', label: 'We actually do okay' },
        ],
      },
    ],
  },
  rate_self: {
    slug: 'rate_self',
    title: 'Rate my coparenting',
    tagline: 'Four taps. See how you show up. We never ask for names.',
    scaleLeft: 'checked out',
    scaleMid: 'mixed',
    scaleRight: 'showing up',
    questions: [
      ROLE_QUESTION,
      {
        key: 'when_they_text',
        prompt: 'When they text you',
        options: [
          { key: 'same_day', label: 'Same day' },
          { key: 'when_you_can', label: 'When you can' },
          { key: 'quiet', label: 'You go quiet' },
          { key: 'novel', label: 'You write a novel' },
        ],
      },
      {
        key: 'your_pickup',
        prompt: 'Your side of pickup',
        options: [
          { key: 'on_time', label: "You're on time" },
          { key: 'late', label: "You're often late" },
          { key: 'missed', label: "You've missed some" },
          { key: 'no_plan', label: "There's no real plan" },
        ],
      },
      {
        key: 'working_on',
        prompt: "What you're working on",
        options: [
          { key: 'answering', label: 'Answering faster' },
          { key: 'on_time', label: 'Being on time' },
          { key: 'keeping_short', label: 'Keeping it short' },
          { key: 'doing_okay', label: 'We actually do okay' },
        ],
      },
    ],
  },
};

export type CheckCard = {
  public_id: string;
  quiz_slug: string;
  type_key: string;
  type_title: string;
  identity_line: string;
  identity_highlights: string[];
  trait_line: string;
  trait_highlights: string[];
  scale_left: string;
  scale_mid: string;
  scale_right: string;
  scale_position: number;
  histogram: { bucket: string; count: number }[];
  early_look: boolean;
  pool_label: string;
  cta_label: string;
  cta_href: string;
  og_title: string;
  og_description: string;
  kicker?: string;
  og_brand?: string;
};

function highlightText(text: string, highlights: string[]): { text: string; hi: boolean }[] {
  if (!highlights.length) return [{ text, hi: false }];
  const escaped = highlights
    .filter(Boolean)
    .map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!escaped.length) return [{ text, hi: false }];
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  return text.split(re).filter(Boolean).map((part) => {
    const hi = highlights.some((h) => h.toLowerCase() === part.toLowerCase());
    return { text: part, hi };
  });
}

export function splitHighlighted(text: string, highlights: string[]) {
  return highlightText(text, highlights);
}
